// Templates view — list, create, duplicate, archive, refresh-thumbnail,
// mark-updated. Owner + staff roles both have full CRUD here.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Copy, Archive, RefreshCw, Edit2, Clock } from 'lucide-react';
import {
  listTemplates,
  createTemplate,
  patchTemplate,
  duplicateTemplate,
  refreshThumbnail,
  markTemplateUpdated,
  ApiError,
} from '../lib/api';
import type { Template } from '../lib/types';

interface Props {
  shopDomain: string;
  role: 'owner' | 'staff';
}

export function TemplatesView({ shopDomain, role: _role }: Props) {
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const templatesQuery = useQuery({
    queryKey: ['templates', { archived: showArchived, shop: shopDomain }],
    queryFn: () => listTemplates({ archived: showArchived, shop: shopDomain }),
  });

  const dupeMut = useMutation({
    mutationFn: (id: string) => duplicateTemplate(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : (err as Error).message),
  });

  const archiveMut = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      patchTemplate(id, { archived }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  const refreshMut = useMutation({
    mutationFn: (id: string) => refreshThumbnail(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  const markUpdatedMut = useMutation({
    mutationFn: (id: string) => markTemplateUpdated(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  return (
    <div className="view">
      <header className="view-head">
        <div>
          <h1>Studio templates</h1>
          <p className="sub">Curated library of pre-built DH projects to attach to customers.</p>
        </div>
        <div className="view-head__actions">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            <span>Show archived</span>
          </label>
          <button className="btn btn--primary" onClick={() => setCreating(true)}>
            <Plus size={14} /> New template
          </button>
        </div>
      </header>

      {error && <div className="error-banner">{error}</div>}

      {templatesQuery.isLoading ? (
        <div className="state">Loading…</div>
      ) : templatesQuery.isError ? (
        <div className="state error">{(templatesQuery.error as Error).message}</div>
      ) : templatesQuery.data!.templates.length === 0 ? (
        <div className="state">
          <p>No templates yet.</p>
          <p>Build a design in DH, then click "+ New template" to register it here.</p>
        </div>
      ) : (
        <div className="grid">
          {templatesQuery.data!.templates.map((t) => (
            <TemplateCard
              key={t.id}
              t={t}
              onDuplicate={() => dupeMut.mutate(t.id)}
              onArchive={() => archiveMut.mutate({ id: t.id, archived: !t.archived_at })}
              onRefresh={() => refreshMut.mutate(t.id)}
              onMarkUpdated={() => markUpdatedMut.mutate(t.id)}
              busy={
                dupeMut.isPending ||
                archiveMut.isPending ||
                refreshMut.isPending ||
                markUpdatedMut.isPending
              }
            />
          ))}
        </div>
      )}

      {creating && (
        <CreateModal
          shopDomain={shopDomain}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void qc.invalidateQueries({ queryKey: ['templates'] });
          }}
        />
      )}
    </div>
  );
}

function TemplateCard({
  t,
  onDuplicate,
  onArchive,
  onRefresh,
  onMarkUpdated,
  busy,
}: {
  t: Template;
  onDuplicate: () => void;
  onArchive: () => void;
  onRefresh: () => void;
  onMarkUpdated: () => void;
  busy: boolean;
}) {
  const [editing, setEditing] = useState(false);
  return (
    <article className={`tpl-card${t.archived_at ? ' is-archived' : ''}`}>
      <div className="tpl-card__thumb">
        {t.thumbnail_url ? <img src={t.thumbnail_url} alt={t.name} /> : <div className="tpl-card__noimg" />}
      </div>
      <div className="tpl-card__body">
        <h3>{t.name}</h3>
        <div className="tpl-card__meta">
          <span className="tag">{t.dh_brand}</span>
          {t.product_type && <span className="tag tag--soft">{t.product_type}</span>}
        </div>
        {t.description && <p className="tpl-card__desc">{t.description}</p>}
        <p className="tpl-card__updated">
          Master updated {new Date(t.master_updated_at).toLocaleDateString()}
        </p>
        <div className="tpl-card__actions">
          <button onClick={() => setEditing(true)} title="Edit metadata" disabled={busy}>
            <Edit2 size={14} />
          </button>
          <button onClick={onDuplicate} title="Duplicate" disabled={busy}>
            <Copy size={14} />
          </button>
          <button onClick={onRefresh} title="Refresh thumbnail" disabled={busy}>
            <RefreshCw size={14} />
          </button>
          <button onClick={onMarkUpdated} title="Mark master updated" disabled={busy}>
            <Clock size={14} />
          </button>
          <button onClick={onArchive} title={t.archived_at ? 'Restore' : 'Archive'} disabled={busy}>
            <Archive size={14} />
          </button>
        </div>
      </div>
      {editing && <EditModal t={t} onClose={() => setEditing(false)} />}
    </article>
  );
}

function EditModal({ t, onClose }: { t: Template; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(t.name);
  const [description, setDescription] = useState(t.description ?? '');
  const [productTitle, setProductTitle] = useState(t.product_title ?? '');
  const [productType, setProductType] = useState(t.product_type ?? '');
  const [variantId, setVariantId] = useState(String(t.shopify_variant_id));

  const saveMut = useMutation({
    mutationFn: () =>
      patchTemplate(t.id, {
        name,
        description: description || null,
        product_title: productTitle || null,
        product_type: productType || null,
        shopify_variant_id: Number(variantId),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['templates'] });
      onClose();
    },
  });

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Edit template</h3>
        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          Description
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label>
          Product title
          <input value={productTitle} onChange={(e) => setProductTitle(e.target.value)} />
        </label>
        <label>
          Product type
          <input value={productType} onChange={(e) => setProductType(e.target.value)} />
        </label>
        <label>
          Shopify variant ID
          <input value={variantId} onChange={(e) => setVariantId(e.target.value)} />
        </label>
        <div className="modal__actions">
          <button onClick={onClose}>Cancel</button>
          <button
            className="btn btn--primary"
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
          >
            {saveMut.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateModal({
  shopDomain,
  onClose,
  onSaved,
}: {
  shopDomain: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    dh_project_id: '',
    dh_brand: 'portal' as 'portal' | 'md' | 'hk',
    shopify_variant_id: '',
    product_handle: '',
    product_title: '',
    product_type: '',
    suggested_custom_name: '',
  });
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      createTemplate({
        ...form,
        shopify_shop_domain: shopDomain,
        shopify_variant_id: Number(form.shopify_variant_id),
      }),
    onSuccess: onSaved,
    onError: (err) => setError(err instanceof ApiError ? err.message : (err as Error).message),
  });

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>New template</h3>
        {error && <div className="error-banner">{error}</div>}
        <label>
          Name *
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label>
          Description
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </label>
        <label>
          DH project ID *
          <input
            value={form.dh_project_id}
            onChange={(e) => setForm({ ...form, dh_project_id: e.target.value })}
          />
        </label>
        <label>
          DH brand *
          <select
            value={form.dh_brand}
            onChange={(e) =>
              setForm({ ...form, dh_brand: e.target.value as 'portal' | 'md' | 'hk' })
            }
          >
            <option value="portal">portal</option>
            <option value="md">md</option>
            <option value="hk">hk</option>
          </select>
        </label>
        <label>
          Shopify variant ID *
          <input
            value={form.shopify_variant_id}
            onChange={(e) => setForm({ ...form, shopify_variant_id: e.target.value })}
          />
        </label>
        <label>
          Product handle
          <input
            value={form.product_handle}
            onChange={(e) => setForm({ ...form, product_handle: e.target.value })}
          />
        </label>
        <label>
          Product title
          <input
            value={form.product_title}
            onChange={(e) => setForm({ ...form, product_title: e.target.value })}
          />
        </label>
        <label>
          Product type
          <input
            value={form.product_type}
            onChange={(e) => setForm({ ...form, product_type: e.target.value })}
          />
        </label>
        <label>
          Suggested custom name
          <input
            value={form.suggested_custom_name}
            onChange={(e) => setForm({ ...form, suggested_custom_name: e.target.value })}
          />
        </label>
        <div className="modal__actions">
          <button onClick={onClose}>Cancel</button>
          <button
            className="btn btn--primary"
            onClick={() => createMut.mutate()}
            disabled={
              createMut.isPending || !form.name || !form.dh_project_id || !form.shopify_variant_id
            }
          >
            {createMut.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
